import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { auth } from "@/lib/api";

// Pages
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Anagrafica from "@/pages/Anagrafica";
import GestioneBadge from "@/pages/GestioneBadge";
import ControlloAccessi from "@/pages/ControlloAccessi";
import Organizzazioni from "@/pages/Organizzazioni";
import CucineCampo from "@/pages/CucineCampo";
import Mezzi from "@/pages/Mezzi";
import Risorse from "@/pages/Risorse";
import Operazioni from "@/pages/Operazioni";
import Reportistica from "@/pages/Reportistica";
import Privacy from "@/pages/Privacy";
import GestioneUtenti from "@/pages/GestioneUtenti";
import AccessiMobile from './pages/AccessiMobile';
import Impostazioni from "@/pages/Impostazioni";
import GateMobile from "@/pages/GateMobile";
import GateMensa from "@/pages/GateMensa";
import GateAccess from "@/pages/GateAccess";

// Layout
import AppLayout from "@/components/layout/AppLayout";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

// Role-based route access configuration
// admin: full access to everything
// operator: operational pages (no user management, no settings)
// user: read-only dashboard and basic pages
const ROLE_ACCESS = {
  "/": ["admin", "operator", "user"],
  "/anagrafica": ["admin"],
  "/gestione-badge": ["admin"],
  "/controllo-accessi": ["admin"],
  "/organizzazioni": ["admin"],
  "/cucine-campo": ["admin"],
  "/mezzi": ["admin"],
  "/risorse": ["admin"],
  "/operazioni": ["admin"],
  "/reportistica": ["admin"],
  "/privacy": ["admin"],
  "/gestione-utenti": ["admin"],
  "/impostazioni": ["admin"],
  "/accessi-mobile": ["admin"],
  "/gate": ["admin", "operator", "user"],
  "/gate-mensa": ["admin", "operator", "user"],
};

// Protected Route wrapper
function ProtectedRoute({ children, allowedRoles }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    auth.me()
      .then((userData) => {
        setUser(userData);
        setIsAuthenticated(true);
      })
      .catch(() => {
        setIsAuthenticated(false);
        navigate('/login');
      });
  }, [navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B1E3D]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Role check - if allowedRoles specified and user role not in list, redirect to dashboard
  if (allowedRoles && user?.role && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <AppLayout user={user}>{children}</AppLayout>;
}

// Main Router
function AppRouter() {
  // Basename for subdirectory deployment
  const basename = process.env.REACT_APP_BASENAME || '';
  
  return (
    <BrowserRouter basename={basename}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/"]}><Dashboard /></ProtectedRoute>} />
        <Route path="/anagrafica" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/anagrafica"]}><Anagrafica /></ProtectedRoute>} />
        <Route path="/gestione-badge" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/gestione-badge"]}><GestioneBadge /></ProtectedRoute>} />
        <Route path="/controllo-accessi" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/controllo-accessi"]}><ControlloAccessi /></ProtectedRoute>} />
        <Route path="/organizzazioni" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/organizzazioni"]}><Organizzazioni /></ProtectedRoute>} />
        <Route path="/cucine-campo" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/cucine-campo"]}><CucineCampo /></ProtectedRoute>} />
        <Route path="/mezzi" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/mezzi"]}><Mezzi /></ProtectedRoute>} />
        <Route path="/risorse" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/risorse"]}><Risorse /></ProtectedRoute>} />
        <Route path="/operazioni" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/operazioni"]}><Operazioni /></ProtectedRoute>} />
        <Route path="/reportistica" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/reportistica"]}><Reportistica /></ProtectedRoute>} />
        <Route path="/privacy" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/privacy"]}><Privacy /></ProtectedRoute>} />
        <Route path="/gestione-utenti" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/gestione-utenti"]}><GestioneUtenti /></ProtectedRoute>} />
        <Route path="/impostazioni" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/impostazioni"]}><Impostazioni /></ProtectedRoute>} />
        <Route path="/accessi-mobile" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/accessi-mobile"]}><AccessiMobile /></ProtectedRoute>} />
        <Route path="/gate" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/gate"]}><GateMobile /></ProtectedRoute>} />
            <Route path="/gate-mensa" element={<ProtectedRoute allowedRoles={ROLE_ACCESS["/gate-mensa"]}><GateMensa /></ProtectedRoute>} />
        <Route path="/gate-access" element={<GateAccess />} />
            <Route path="/gate-access/:code" element={<GateAccess />} />
            <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppRouter />
    </QueryClientProvider>
  );
}

export default App;
